import React, { useCallback, useMemo } from 'react';
import { Fancybox } from '@fancyapps/ui';
import { PhotoModel } from '../../../types/protocols';
import { useFirebaseImage, getImagePathWithSize, ImageSize, resolveFirebaseImageUrl } from '../../../Helpers/ImageLoader';
import { useUsersData } from '../../../Helpers/UsersContext';
import { buildPhotoCaption } from '../../../Helpers/photoCaptionUtils';
import { orderPhotos } from '../../../Helpers/photoOrdering';
import { useInView } from '../../../hooks/useInView';
import './ValidationPhotos.css';

const photoPath = (photo: PhotoModel, buildingKey: string): string =>
    photo.storagePath || `buildings/${buildingKey}/photos/${photo.key}`;

// Lazy-loaded thumbnail; THUMBNAIL_SMALL (200x200) keeps long lists light
const PhotoThumbnail = React.memo<{
    photo: PhotoModel;
    buildingKey: string;
    onClick?: () => void;
}>(({ photo, buildingKey, onClick }) => {
    const { ref, inView } = useInView<HTMLButtonElement>();
    const thumbnailPath = inView ? getImagePathWithSize(photoPath(photo, buildingKey), ImageSize.THUMBNAIL_SMALL) : null;
    const { src, isLoading } = useFirebaseImage(thumbnailPath);

    return (
        <button
            ref={ref}
            type="button"
            className="validation-thumbnail"
            onClick={onClick}
            disabled={!onClick}
            aria-label="Visa foto"
        >
            {inView && !isLoading && src ? (
                <img src={src} alt="" />
            ) : (
                <span className="thumbnail-placeholder" />
            )}
        </button>
    );
});

/**
 * Every photo of one component as thumbnails, newest first. A click opens
 * Fancybox on that photo with the component's other photos as the gallery.
 * With `static` the thumbnails are not clickable (confirmation dialogs).
 */
export const ValidationPhotos: React.FC<{
    photos: { [key: string]: PhotoModel } | undefined;
    buildingKey: string;
    static?: boolean;
}> = ({ photos, buildingKey, static: isStatic }) => {
    const { users } = useUsersData();
    const ordered = useMemo(() => orderPhotos(photos || {}), [photos]);

    const openGallery = useCallback(async (startIndex: number) => {
        // Resolve real URLs first: a slide opened on a placeholder is cached by Fancybox
        const slides = (await Promise.all(
            ordered.map(async (photo) => {
                try {
                    return {
                        src: await resolveFirebaseImageUrl(photoPath(photo, buildingKey)),
                        caption: buildPhotoCaption(photo, users),
                    };
                } catch {
                    return null;
                }
            })
        ));
        const clicked = slides[startIndex];
        const available = slides.filter((slide): slide is NonNullable<typeof slide> => slide !== null);
        if (!clicked) return;

        Fancybox.show(available, {
            animated: true,
            showClass: 'fancybox-fadeIn',
            startIndex: available.indexOf(clicked),
        });
    }, [ordered, buildingKey, users]);

    if (ordered.length === 0) {
        return <span className="no-photos">Inga foton</span>;
    }

    return (
        <>
            {ordered.map((photo, index) => (
                <PhotoThumbnail
                    key={photo.key}
                    photo={photo}
                    buildingKey={buildingKey}
                    onClick={isStatic ? undefined : () => openGallery(index)}
                />
            ))}
        </>
    );
};
